import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { rootUrl } from '../../config/config';
import TopBar from './home/TopBar';
import KpiStrip from './home/KpiStrip';
import FloatingActionButton from './home/FloatingActionButton';
import PendingJobCards from './home/widgets/PendingJobCards';
import PendingBalances from './home/widgets/PendingBalances';
import InsuranceExpiring from './home/widgets/InsuranceExpiring';
import LicensesExpiring from './home/widgets/LicensesExpiring';
import TopCars from './home/widgets/TopCars';

const token = () => localStorage.getItem('authToken') || '';
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

export default function Home() {
  const [invoiceCoveragePct, setInvoiceCoveragePct] = useState(null);
  const [totalOutstanding, setTotalOutstanding] = useState(null);
  const [pendingJobCards, setPendingJobCards] = useState([]);
  const [pendingBalances, setPendingBalances] = useState([]);
  const [insuranceExpiring, setInsuranceExpiring] = useState([]);
  const [licensesExpiring, setLicensesExpiring] = useState([]);

  const [topCars, setTopCars] = useState([]);
  const [loadingCars, setLoadingCars] = useState(true);
  const [jobCardTotal, setJobCardTotal] = useState(null);
  const [licenseTotal, setLicenseTotal] = useState(null);

  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [loadingInsurance, setLoadingInsurance] = useState(true);
  const [loadingLicenses, setLoadingLicenses] = useState(true);

  const fetchAll = useCallback(async () => {
    // Step 1: week key (required for coverage calc)
    let week = null;
    try {
      const weekRes = await axios.get(`${rootUrl}/weekly-summary/weeks`, auth());
      week = weekRes.data?.[0]?.week ?? null;
    } catch (_) {}

    // Step 2: parallel fetches
    const results = await Promise.allSettled([
      // [0] cars for coverage (include_driver attaches active driver details)
      axios.get(`${rootUrl}/cars?include_driver=true`, auth()),
      // [1] weekly type-totals for coverage
      week ? axios.get(`${rootUrl}/weekly-summary/${week}/type-totals`, auth()) : Promise.resolve(null),
      // [2] pending job cards
      axios.get(`${rootUrl}/api/job-cards?invoiced=false&limit=5`, auth()),
      // [3] driver pending balances
      axios.get(`${rootUrl}/drivers/pending-balances?limit=5`, auth()),
      // [4] insurance expiring
      axios.get(`${rootUrl}/cars/insurance/expiring?days=30`, auth()),
      // [5] licenses expiring
      axios.get(`${rootUrl}/drivers?licenseStatus=expiring&limit=5`, auth()),
    ]);

    // Coverage + Top Cars
    const carsOk = results[0].status === 'fulfilled';
    const totalsOk = results[1].status === 'fulfilled' && results[1].value;
    if (carsOk) {
      const cars = results[0].value.data || [];
      // Top 10 cars by outstanding balance
      const sorted = [...cars]
        .filter(c => parseFloat(c.actual_bal || 0) > 0)
        .sort((a, b) => parseFloat(b.actual_bal) - parseFloat(a.actual_bal))
        .slice(0, 10);
      setTopCars(sorted);
      if (totalsOk) {
        const totalOutstanding = cars.reduce((sum, c) => sum + parseFloat(c.actual_bal || 0), 0);
        const typeTotals = Array.isArray(results[1].value.data) ? results[1].value.data : (results[1].value.data?.data || []);
        const waiTotal = typeTotals.reduce((sum, t) => {
          const type = String(t.type || t.trans_type || t.name || '').toUpperCase();
          return type === 'WAI' ? sum + parseFloat(t.total_amount || 0) : sum;
        }, 0);
        setTotalOutstanding(totalOutstanding);
        setInvoiceCoveragePct(waiTotal > 0 ? Number(((totalOutstanding / waiTotal) * 100).toFixed(2)) : null);
      }
    }
    setLoadingCars(false);

    // Job cards
    if (results[2].status === 'fulfilled') {
      const raw = results[2].value.data;
      setPendingJobCards(Array.isArray(raw) ? raw : (raw?.data || []));
      setJobCardTotal(typeof raw?.total === 'number' ? raw.total : null);
    }
    setLoadingJobs(false);

    // Pending balances
    if (results[3].status === 'fulfilled') {
      setPendingBalances(results[3].value.data?.data || []);
    }
    setLoadingBalances(false);

    // Insurance
    if (results[4].status === 'fulfilled') {
      const ins = results[4].value.data;
      setInsuranceExpiring(Array.isArray(ins?.data) ? ins.data : (Array.isArray(ins) ? ins : []));
    }
    setLoadingInsurance(false);

    // Licenses
    if (results[5].status === 'fulfilled') {
      const lic = results[5].value.data;
      setLicensesExpiring(Array.isArray(lic?.data) ? lic.data : (Array.isArray(lic) ? lic : []));
      setLicenseTotal(lic?.pagination?.totalCount ?? null);
    }
    setLoadingLicenses(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <TopBar />
        <KpiStrip
          invoiceCoveragePct={invoiceCoveragePct}
          totalOutstanding={totalOutstanding}
          pendingJobCards={pendingJobCards}
          jobCardTotal={jobCardTotal}
          insuranceExpiring={insuranceExpiring}
          licensesExpiring={licensesExpiring}
          licenseTotal={licenseTotal}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          <PendingJobCards items={pendingJobCards} loading={loadingJobs} />
          <PendingBalances items={pendingBalances} loading={loadingBalances} />
          <InsuranceExpiring items={insuranceExpiring} loading={loadingInsurance} />
          <LicensesExpiring items={licensesExpiring} loading={loadingLicenses} />
          <div className="lg:col-span-2">
            <TopCars items={topCars} loading={loadingCars} />
          </div>
        </div>
      </div>
      <FloatingActionButton />
    </div>
  );
}
